import { useCallback, useEffect, useState } from "react";
import {
  getDeferredPrompt,
  isAndroid,
  isIOS,
  isMobile,
  isSafari,
  isStandalone,
  promptInstall as runPromptInstall,
  subscribePwa,
  wasInstalledThisSession,
} from "../utils/pwa";

/** How the current device can install the app, if at all. */
export type InstallMethod = "prompt" | "ios" | "none";

export interface PwaInstallState {
  /** Running as an installed app (standalone / home-screen icon). */
  isStandalone: boolean;
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  /** Native install prompt is available right now (Android / desktop Chromium). */
  canPrompt: boolean;
  /** An install path exists at all (native prompt OR iOS manual instructions). */
  installAvailable: boolean;
  installMethod: InstallMethod;
  /** Fire the native prompt; resolves with the user's choice. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

/**
 * Reactive view over the module-level PWA state in `utils/pwa`.
 * Re-renders when the install prompt becomes available or the app is installed.
 */
export function usePwaInstall(): PwaInstallState {
  const [, bump] = useState(0);
  useEffect(() => subscribePwa(() => bump((n) => n + 1)), []);

  const standalone = isStandalone() || wasInstalledThisSession();
  const ios = isIOS();
  const safari = isSafari();
  const canPrompt = !!getDeferredPrompt() && !standalone;

  // On iOS the only install path is Safari's Share → "Add to Home Screen".
  const iosInstallable = ios && safari && !standalone;

  const installMethod: InstallMethod = canPrompt ? "prompt" : iosInstallable ? "ios" : "none";

  const promptInstall = useCallback(() => runPromptInstall(), []);

  return {
    isStandalone: standalone,
    isMobile: isMobile(),
    isIOS: ios,
    isAndroid: isAndroid(),
    isSafari: safari,
    canPrompt,
    installAvailable: installMethod !== "none",
    installMethod,
    promptInstall,
  };
}
